import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Trash2, FileText, CalendarDays, ArrowRight, Check } from "lucide-react";
import { fmtStopTime } from "@/lib/tripDetails";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const shortAddr = (a = "") => (a || "").split(",")[0] || "—";
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const timeOf = (iso = "") => fmtStopTime((iso || "").slice(11, 16)) || "—";
const emptyManual = () => ({ time: "", tripType: "point_to_point", hours: "", pickup: "", stops: [], dropoff: "", amount: "", chauffeur: "" });

/**
 * End-of-Day consolidated invoice: one client, one service date, every trip
 * of the day as its own line. Lines come from existing reservations (search +
 * one-click add) and/or trips typed in by hand.
 */
export default function EndOfDayInvoice({ onCreated }) {
  const [client, setClient] = useState({ name: "", email: "", phone: "" });
  const [date, setDate] = useState(todayLocal());
  const [candidates, setCandidates] = useState(null); // null = not searched yet
  const [showAll, setShowAll] = useState(false);
  const [added, setAdded] = useState([]);          // existing bookings added to the invoice
  const [manual, setManual] = useState([]);        // hand-entered trips
  const [draft, setDraft] = useState(emptyManual());
  const [gratuity, setGratuity] = useState("");
  const [discount, setDiscount] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [markPaid, setMarkPaid] = useState(false);
  const [payMethod, setPayMethod] = useState("zelle");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const hasContact = client.email.trim() || client.phone.trim();

  const findTrips = async (all = showAll) => {
    if (!hasContact) return toast.error("Enter the client's email or phone first");
    setBusy(true);
    try {
      const r = await api.get("/invoices/end-of-day/trips", {
        params: { date, email: client.email.trim() || undefined, phone: client.phone.trim() || undefined, all: all || undefined },
      });
      setCandidates(r.data || []);
      if (!client.name && r.data?.[0]?.customer_name) setClient(c => ({ ...c, name: r.data[0].customer_name }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load trips");
    } finally { setBusy(false); }
  };
  useEffect(() => { if (candidates !== null) findTrips(showAll); /* eslint-disable-next-line */ }, [showAll]);

  const addExisting = (t) => {
    if (added.some(a => a.booking_id === t.booking_id)) return;
    setAdded(arr => [...arr, t]);
    toast.success(`Added ${t.confirmation_code}`);
  };
  const removeExisting = (id) => setAdded(arr => arr.filter(a => a.booking_id !== id));

  const addManual = () => {
    const amt = Number(draft.amount);
    if (!draft.pickup.trim()) return toast.error("Pickup address is required");
    if (draft.tripType !== "hourly" && !draft.dropoff.trim()) return toast.error("Drop-off address is required");
    if (!(amt > 0)) return toast.error("Enter the amount for this trip");
    setManual(arr => [...arr, { ...draft, amount: amt }]);
    setDraft(emptyManual());
  };
  const removeManual = (i) => setManual(arr => arr.filter((_, idx) => idx !== i));

  const lines = useMemo(() => {
    const ex = added.map(t => ({
      key: t.booking_id, time: (t.pickup_datetime || "").slice(11, 16), label: timeOf(t.pickup_datetime),
      route: t.trip_type === "hourly" ? `Hourly charter from ${shortAddr(t.pickup_address)}${t.hours ? ` · ${t.hours} hr` : ""}`
        : [t.pickup_address, ...(t.stops || []), t.dropoff_address].filter(Boolean).map(shortAddr).join(" → "),
      meta: [t.confirmation_code, t.vehicle_name, t.assigned_driver_name].filter(Boolean).join(" · "),
      amount: Number(t.fare || 0), remove: () => removeExisting(t.booking_id), kind: "existing",
    }));
    const mn = manual.map((m, i) => ({
      key: `m${i}`, time: m.time, label: fmtStopTime(m.time) || "—",
      route: m.tripType === "hourly" ? `Hourly charter from ${shortAddr(m.pickup)}${m.hours ? ` · ${m.hours} hr` : ""}`
        : [m.pickup, ...m.stops, m.dropoff].filter(Boolean).map(shortAddr).join(" → "),
      meta: ["Manually added", m.chauffeur].filter(Boolean).join(" · "),
      amount: Number(m.amount || 0), remove: () => removeManual(i), kind: "manual",
    }));
    return [...ex, ...mn].sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99"));
  }, [added, manual]);

  const subtotal = lines.reduce((a, l) => a + l.amount, 0);
  const total = Math.max(0, subtotal - (Number(discount) || 0) + (Number(gratuity) || 0));

  const payload = () => ({
    customer_name: client.name.trim() || null,
    customer_email: client.email.trim() || null,
    customer_phone: client.phone.trim() || null,
    service_date: date,
    trip_ids: added.map(t => t.booking_id),
    manual_trips: manual.map(m => ({
      pickup_time: m.time || null, trip_type: m.tripType,
      hours: m.tripType === "hourly" && Number(m.hours) > 0 ? Number(m.hours) : null,
      pickup_address: m.pickup.trim(), dropoff_address: m.tripType === "hourly" ? null : m.dropoff.trim(),
      stops: m.stops.map(s => s.trim()).filter(Boolean), amount: Number(m.amount),
      chauffeurs: m.chauffeur.trim() ? [{ name: m.chauffeur.trim() }] : null,
    })),
    gratuity: Number(gratuity) || 0, discount: Number(discount) || 0,
    notes: notes.trim() || null, due_date: dueDate || null,
    mark_paid: markPaid, payment_method: markPaid ? payMethod : null,
  });

  const validate = () => {
    if (!hasContact) { toast.error("Enter the client's email or phone"); return false; }
    if (lines.length === 0) { toast.error("Add at least one trip"); return false; }
    return true;
  };
  const preview = async () => {
    if (!validate()) return;
    setPreviewing(true);
    try {
      const r = await api.post("/invoices/end-of-day/preview-pdf", payload(), { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch (e) {
      let msg = "Preview failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setPreviewing(false); }
  };
  const create = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const r = await api.post("/invoices/end-of-day", payload());
      toast.success(`End-of-day invoice ${r.data.invoice_number} created — ${money(r.data.total)}`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="eod-invoice">
      <ClientAndDate client={client} setClient={setClient} date={date} setDate={setDate} onFind={() => findTrips()} busy={busy}/>

      {candidates !== null && (
        <CandidateTrips candidates={candidates} added={added} onAdd={addExisting} showAll={showAll} setShowAll={setShowAll}/>
      )}

      <ManualTripForm draft={draft} setDraft={setDraft} onAdd={addManual}/>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <LineItems lines={lines} subtotal={subtotal}/>
          <div className="border border-white/10 bg-[#0A0A0A] p-5 space-y-4">
            <div className="overline">4 · Details</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="field-label">Gratuity (optional)</label>
                <input data-testid="eod-gratuity" type="number" min="0" step="0.01" className="field-input" placeholder="0.00" value={gratuity} onChange={e => setGratuity(e.target.value)}/>
              </div>
              <div>
                <label className="field-label">Discount (optional)</label>
                <input data-testid="eod-discount" type="number" min="0" step="0.01" className="field-input" placeholder="0.00" value={discount} onChange={e => setDiscount(e.target.value)}/>
              </div>
              <div>
                <label className="field-label">Due date</label>
                <input data-testid="eod-due-date" type="date" className="field-input [color-scheme:dark]" value={dueDate} onChange={e => setDueDate(e.target.value)}/>
              </div>
            </div>
            <div>
              <label className="field-label">Notes (shown on invoice)</label>
              <textarea data-testid="eod-notes" className="field-input min-h-[60px]" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Thank you for riding with Delux Limo today"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#F5F2EB]">
              <input data-testid="eod-mark-paid" type="checkbox" checked={markPaid} onChange={e => setMarkPaid(e.target.checked)} className="accent-[#D4AF37]"/>
              Already paid — mark as paid on create
            </label>
            {markPaid && (
              <select data-testid="eod-pay-method" className="field-input" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                {["zelle", "venmo", "cash", "card", "check", "other"].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 space-y-3 h-fit">
          <div className="overline">Grand total</div>
          <SummaryRow k="Trips" v={String(lines.length)}/>
          <SummaryRow k="Subtotal" v={money(subtotal)}/>
          {Number(discount) > 0 && <SummaryRow k="Discount" v={`−${money(discount)}`}/>}
          {Number(gratuity) > 0 && <SummaryRow k="Gratuity" v={money(gratuity)}/>}
          <div className="border-t border-[#D4AF37]/40 pt-3 flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">Total</span>
            <span className="text-2xl text-[#D4AF37] font-semibold" data-testid="eod-total">{money(total)}</span>
          </div>
          <button data-testid="eod-preview-pdf-btn" disabled={previewing} onClick={preview} className="btn-ghost w-full justify-center"><FileText size={13}/> {previewing ? "Rendering…" : "Preview PDF"}</button>
          <button data-testid="eod-create-btn" disabled={busy} onClick={create} className="btn-gold w-full">{busy ? "Working…" : "Create end-of-day invoice"}</button>
          <div className="text-[10px] text-[#71717A] uppercase tracking-[0.18em]">Next: email or text the pay link from the invoice</div>
        </div>
      </div>
    </div>
  );
}

function ClientAndDate({ client, setClient, date, setDate, onFind, busy }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/customers/search", { params: { q: q.trim() } }).then(r => setResults(r.data || [])).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  const pick = (c) => {
    setClient({ name: c.name || "", email: c.email || "", phone: c.phone || "" });
    setQ(""); setResults([]);
  };
  const set = (k) => (e) => setClient(c => ({ ...c, [k]: e.target.value }));
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 relative" data-testid="eod-client-card">
      <div className="overline mb-1">1 · Client &amp; service date</div>
      <p className="text-[11px] text-[#71717A] mb-3">Pick a client, choose the day, then pull their trips or add them by hand.</p>
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]"/>
        <input data-testid="eod-client-search" className="field-input pl-9" placeholder="Search clients by name, email or phone…" value={q} onChange={e => setQ(e.target.value)}/>
      </div>
      {results.length > 0 && (
        <div className="absolute left-5 right-5 z-20 bg-[#111] border border-[#D4AF37]/40 max-h-56 overflow-y-auto shadow-xl" data-testid="eod-client-results">
          {results.map(c => (
            <button key={c.user_id} data-testid={`eod-client-pick-${c.user_id}`} onClick={() => pick(c)}
              className="w-full text-left px-3 py-2.5 border-b border-white/5 last:border-b-0 hover:bg-[#D4AF37]/10 text-sm">
              <span className="text-[#F5F2EB]">{c.name}</span>
              <span className="text-[#71717A] text-xs ml-2">{[c.email, c.phone].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
        <div><label className="field-label">Client name</label><input data-testid="eod-name" className="field-input" value={client.name} onChange={set("name")} placeholder="Jane Doe"/></div>
        <div><label className="field-label">Email</label><input data-testid="eod-email" className="field-input" value={client.email} onChange={set("email")} placeholder="client@example.com"/></div>
        <div><label className="field-label">Phone</label><input data-testid="eod-phone" className="field-input" value={client.phone} onChange={set("phone")} placeholder="(414) 555-0123"/></div>
        <div><label className="field-label">Service date</label><input data-testid="eod-date" type="date" className="field-input [color-scheme:dark]" value={date} onChange={e => setDate(e.target.value)}/></div>
      </div>
      <button data-testid="eod-find-trips-btn" disabled={busy} onClick={onFind} className="btn-ghost !py-2.5 !px-4 text-[11px] mt-4">
        <CalendarDays size={13}/> Find this client's trips on {date}
      </button>
    </div>
  );
}

function CandidateTrips({ candidates, added, onAdd, showAll, setShowAll }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="eod-candidates">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="overline">2 · Trips in the system <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">({candidates.length} found)</span></div>
        <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
          <input data-testid="eod-show-all" type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="accent-[#D4AF37]"/>
          Show already paid / invoiced trips
        </label>
      </div>
      {candidates.length === 0 ? (
        <div className="text-sm text-[#71717A]" data-testid="eod-no-candidates">No {showAll ? "" : "unpaid "}trips for this client on that date — add them manually below.</div>
      ) : (
        <div className="divide-y divide-white/5 border border-white/10">
          {candidates.map(t => {
            const isAdded = added.some(a => a.booking_id === t.booking_id);
            const flagged = t.already_paid || t.on_invoice;
            return (
              <div key={t.booking_id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`eod-candidate-${t.booking_id}`}>
                <div className="w-20 text-[#F5F2EB] text-xs">{timeOf(t.pickup_datetime)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs truncate">
                    <span className="text-[#F5F2EB] truncate">{shortAddr(t.pickup_address)}</span>
                    <ArrowRight size={11} className="text-[#D4AF37] shrink-0"/>
                    <span className="text-[#F5F2EB] truncate">{t.trip_type === "hourly" ? `Hourly · ${t.hours || "?"} hr` : shortAddr(t.dropoff_address)}</span>
                  </div>
                  <div className="text-[10px] text-[#71717A] mt-0.5">
                    <span className="font-mono text-[#D4AF37]">{t.confirmation_code}</span>
                    {t.vehicle_name && ` · ${t.vehicle_name}`}{t.assigned_driver_name && ` · ${t.assigned_driver_name}`}
                    {t.already_paid && <span className="ml-2 text-emerald-400 uppercase tracking-[0.14em]">Paid</span>}
                    {t.on_invoice && <span className="ml-2 text-amber-400 uppercase tracking-[0.14em]">On {t.on_invoice}</span>}
                  </div>
                </div>
                <div className="text-[#D4AF37] text-sm w-20 text-right">{money(t.fare)}</div>
                <button data-testid={`eod-add-trip-${t.booking_id}`} disabled={isAdded || !!t.on_invoice} onClick={() => onAdd(t)}
                  title={t.on_invoice ? "Already on another invoice" : flagged ? "Already paid — add anyway" : "Add to invoice"}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border ${isAdded ? "border-emerald-500/50 text-emerald-400" : "border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10"} disabled:opacity-50 disabled:cursor-not-allowed`}>
                  {isAdded ? <><Check size={11}/> Added</> : <><Plus size={11}/> Add</>}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ManualTripForm({ draft, setDraft, onAdd }) {
  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));
  const hourly = draft.tripType === "hourly";
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid="eod-manual-card">
      <div className="overline mb-1">3 · Add a trip manually <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(trips not booked in the system — each becomes a reservation record)</span></div>
      <div className="flex border border-white/15 w-fit mt-3" role="radiogroup" data-testid="eod-manual-trip-type">
        {[["point_to_point", "Point to Point"], ["hourly", "Hourly Charter"]].map(([k, label]) => (
          <button key={k} type="button" role="radio" aria-checked={draft.tripType === k} data-testid={`eod-manual-type-${k}`}
            onClick={() => setDraft(d => ({ ...d, tripType: k }))}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${draft.tripType === k ? "bg-[#D4AF37] text-[#050505]" : "text-[#A1A1AA] hover:text-[#F5F2EB]"}`}>{label}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-3">
        <div><label className="field-label">Pickup time</label><input data-testid="eod-manual-time" type="time" className="field-input [color-scheme:dark]" value={draft.time} onChange={set("time")}/></div>
        {hourly && <div><label className="field-label">Hours</label><input data-testid="eod-manual-hours" type="number" min="1" step="0.5" className="field-input" value={draft.hours} onChange={set("hours")} placeholder="3"/></div>}
        <div className={hourly ? "md:col-span-2" : "md:col-span-2"}><label className="field-label">{hourly ? "Starting address" : "Pickup address"}</label><input data-testid="eod-manual-pickup" className="field-input" value={draft.pickup} onChange={set("pickup")} placeholder="e.g. MKE Airport"/></div>
        {!hourly && <div className="md:col-span-2"><label className="field-label">Drop-off address</label><input data-testid="eod-manual-dropoff" className="field-input" value={draft.dropoff} onChange={set("dropoff")} placeholder="e.g. Pfister Hotel"/></div>}
        <div><label className="field-label">Amount</label>
          <div className="relative"><span className="absolute left-0 top-1/2 -translate-y-1/2 text-[#71717A] text-sm">$</span>
            <input data-testid="eod-manual-amount" type="number" min="0" step="0.01" className="field-input" style={{ paddingLeft: "1.25rem" }} value={draft.amount} onChange={set("amount")} placeholder="0.00"/></div>
        </div>
      </div>
      {!hourly && draft.stops.map((s, i) => (
        <div key={i} className="flex gap-2 items-center mt-2" data-testid={`eod-manual-stop-${i}`}>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] w-14 shrink-0">Stop {i + 1}</span>
          <input data-testid={`eod-manual-stop-input-${i}`} className="field-input flex-1" value={s} placeholder="Stop address"
            onChange={e => setDraft(d => ({ ...d, stops: d.stops.map((x, idx) => idx === i ? e.target.value : x) }))}/>
          <button data-testid={`eod-manual-stop-remove-${i}`} onClick={() => setDraft(d => ({ ...d, stops: d.stops.filter((_, idx) => idx !== i) }))} className="text-[#71717A] hover:text-red-400 p-1.5"><Trash2 size={14}/></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 mt-3 items-end">
        {!hourly && <button data-testid="eod-manual-add-stop" onClick={() => setDraft(d => ({ ...d, stops: [...d.stops, ""] }))} className="btn-ghost !py-2 !px-3 text-[10px]"><Plus size={12}/> Add stop</button>}
        <div className="flex-1 min-w-[180px]"><label className="field-label">Chauffeur (optional)</label><input data-testid="eod-manual-chauffeur" className="field-input" value={draft.chauffeur} onChange={set("chauffeur")} placeholder="e.g. Marcus J."/></div>
        <button data-testid="eod-manual-add-btn" onClick={onAdd} className="btn-gold !py-2.5 !px-4 text-[11px]"><Plus size={13}/> Add trip to invoice</button>
      </div>
    </div>
  );
}

function LineItems({ lines, subtotal }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A]" data-testid="eod-lines">
      <div className="px-5 pt-5 pb-3 overline">Trips on this invoice <span className="text-[#71717A] normal-case tracking-normal text-[10px] ml-1">(each trip is its own line)</span></div>
      {lines.length === 0 ? (
        <div className="px-5 pb-5 text-sm text-[#71717A]" data-testid="eod-lines-empty">Nothing added yet — pull trips from the system or add one manually.</div>
      ) : (
        <>
          <div className="grid grid-cols-12 px-5 py-2 text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-y border-white/5">
            <div className="col-span-2">Time</div><div className="col-span-7">Trip</div><div className="col-span-2 text-right">Subtotal</div><div className="col-span-1"/>
          </div>
          {lines.map((l, i) => (
            <div key={l.key} className="grid grid-cols-12 px-5 py-3 border-b border-white/5 items-center text-sm" data-testid={`eod-line-${i}`}>
              <div className="col-span-2 text-xs text-[#F5F2EB]">{l.label}</div>
              <div className="col-span-7 min-w-0">
                <div className="text-[13px] text-[#F5F2EB] truncate">Trip {i + 1} — {l.route}</div>
                {l.meta && <div className="text-[10px] text-[#71717A] mt-0.5 truncate">{l.meta}</div>}
              </div>
              <div className="col-span-2 text-right text-[#D4AF37]" data-testid={`eod-line-amount-${i}`}>{money(l.amount)}</div>
              <div className="col-span-1 text-right"><button data-testid={`eod-line-remove-${i}`} onClick={l.remove} className="text-[#71717A] hover:text-red-400 p-1"><Trash2 size={13}/></button></div>
            </div>
          ))}
          <div className="grid grid-cols-12 px-5 py-3 text-sm"><div className="col-span-9 text-right text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">Trips subtotal</div><div className="col-span-2 text-right text-[#F5F2EB]" data-testid="eod-subtotal">{money(subtotal)}</div></div>
        </>
      )}
    </div>
  );
}

function SummaryRow({ k, v }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-[#A1A1AA] text-xs">{k}</span><span className="text-[#F5F2EB]">{v}</span></div>;
}
